import React, { useState, useEffect } from 'react';
import Sidebar from './Sidebar';
import './AdminPrivacy.css';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSave, FaPlus, FaTrash, FaRegFileAlt, FaHeading, FaParagraph, FaListUl } from 'react-icons/fa';
import toast from 'react-hot-toast';
import { safeFetch } from '../utils/apiUtils';

const AdminPrivacy = () => {
    const [isSaving, setIsSaving] = useState(false);
    const [privacyId, setPrivacyId] = useState(null);
    const [privacyData, setPrivacyData] = useState({
        hero: {
            titlePart: 'Your',
            highlight: 'Privacy',
            suffix: 'is Our Priority',
            description: 'At Nimai Solutions, we believe in radical transparency and ethical data handling. This policy outlines how we protect your information.'
        },
        sections: [
            {
                id: Date.now(),
                icon: 'shield',
                title: 'Commitment to Privacy',
                content: 'Nimai Solutions is committed to maintaining the trust and confidence of our visitors to our web site...',
                points: []
            }
        ],
        lastUpdated: 'February 6, 2026'
    });

    useEffect(() => {
        fetchPrivacy();
    }, []);

    const fetchPrivacy = async () => {
        try {
            const { ok, data: result } = await safeFetch('https://aayushrealproperties.in/nimaibackend/public/api/privacy-policies', {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                }
            });
            let privacy = null;

            if (Array.isArray(result)) {
                privacy = result[0];
            } else if (result.data) {
                privacy = Array.isArray(result.data) ? result.data[0] : result.data;
            }

            if (ok && privacy) {
                setPrivacyId(privacy.id);
                setPrivacyData({
                    hero: {
                        titlePart: privacy.hero_title_part1,
                        highlight: privacy.hero_highlight_word,
                        suffix: privacy.hero_title_suffix,
                        description: privacy.hero_description
                    },
                    sections: privacy.sections.map((s, idx) => ({
                        id: s.id || idx,
                        icon: s.icon_type,
                        title: s.section_title,
                        content: s.content,
                        points: s.key_points || []
                    })),
                    lastUpdated: privacy.last_updated_date
                });
            }
        } catch (error) {
            console.error('Error fetching privacy policy:', error);
            toast.error('Failed to load privacy policy');
        }
    };

    const handleSave = async () => {
        setIsSaving(true);
        const method = privacyId ? 'PUT' : 'POST';
        const url = privacyId
            ? `https://aayushrealproperties.in/nimaibackend/public/api/privacy-policies/${privacyId}`
            : 'https://aayushrealproperties.in/nimaibackend/public/api/privacy-policies';

        const payload = {
            hero_title_part1: privacyData.hero.titlePart,
            hero_highlight_word: privacyData.hero.highlight,
            hero_title_suffix: privacyData.hero.suffix,
            hero_description: privacyData.hero.description,
            last_updated_date: privacyData.lastUpdated,
            sections: privacyData.sections.map(s => ({
                icon_type: s.icon,
                section_title: s.title,
                content: s.content,
                key_points: s.points
            }))
        };

        try {
            const { ok, data: result } = await safeFetch(url, {
                method: method,
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('admin_token')}`,
                    'Accept': 'application/json'
                },
                body: JSON.stringify(payload)
            });

            if (ok) {
                if (!privacyId) setPrivacyId(result.id);
                toast.success('Privacy Policy updated successfully!');
            } else {
                toast.error('Failed to update privacy policy');
            }
        } catch (error) {
            console.error('Error saving privacy policy:', error);
            toast.error('Network error while saving');
        } finally {
            setIsSaving(false);
        }
    };

    const addSection = () => {
        const newSection = {
            id: Date.now(),
            icon: 'shield',
            title: 'New Section Title',
            content: 'Section content goes here...',
            points: []
        };
        setPrivacyData({ ...privacyData, sections: [...privacyData.sections, newSection] });
    };

    const removeSection = (id) => {
        setPrivacyData({ ...privacyData, sections: privacyData.sections.filter(s => s.id !== id) });
    };

    const updateSection = (id, field, value) => {
        const newSections = privacyData.sections.map(s => {
            if (s.id === id) return { ...s, [field]: value };
            return s;
        });
        setPrivacyData({ ...privacyData, sections: newSections });
    };

    const addPoint = (sectionId) => {
        const newSections = privacyData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: [...s.points, 'New point'] };
            return s;
        });
        setPrivacyData({ ...privacyData, sections: newSections });
    };

    const removePoint = (sectionId, pointIdx) => {
        const newSections = privacyData.sections.map(s => {
            if (s.id === sectionId) return { ...s, points: s.points.filter((_, i) => i !== pointIdx) };
            return s;
        });
        setPrivacyData({ ...privacyData, sections: newSections });
    };

    const updatePoint = (sectionId, pointIdx, value) => {
        const newSections = privacyData.sections.map(s => {
            if (s.id === sectionId) {
                const newPoints = [...s.points];
                newPoints[pointIdx] = value;
                return { ...s, points: newPoints };
            }
            return s;
        });
        setPrivacyData({ ...privacyData, sections: newSections });
    };

    return (
        <div className="admin-layout">
            <Sidebar />
            <main className="admin-content">
                <header className="content-header">
                    <div>
                        <h1>Edit <span>Privacy Policy</span></h1>
                        <p>Manage your legal transparency and data protection statements.</p>
                    </div>
                    <div className="header-actions">
                        <button className="save-btn" onClick={handleSave} disabled={isSaving}>
                            {isSaving ? 'Saving...' : <><FaSave /> Save Changes</>}
                        </button>
                    </div>
                </header>

                <div className="admin-privacy-container">
                    {/* Hero Section Edit */}
                    <div className="admin-card-section">
                        <div className="section-header">
                            <h3><FaHeading /> Hero Section</h3>
                        </div>
                        <div className="admin-form-grid">
                            <div className="admin-input-group">
                                <label>Title Part 1</label>
                                <input type="text" value={privacyData.hero.titlePart} onChange={(e) => setPrivacyData({ ...privacyData, hero: { ...privacyData.hero, titlePart: e.target.value } })} />
                            </div>
                            <div className="admin-input-group">
                                <label>Highlight Word</label>
                                <input type="text" className="highlight-field" value={privacyData.hero.highlight} onChange={(e) => setPrivacyData({ ...privacyData, hero: { ...privacyData.hero, highlight: e.target.value } })} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Title Suffix</label>
                                <input type="text" value={privacyData.hero.suffix} onChange={(e) => setPrivacyData({ ...privacyData, hero: { ...privacyData.hero, suffix: e.target.value } })} />
                            </div>
                            <div className="admin-input-group full-width">
                                <label>Description</label>
                                <textarea rows="3" value={privacyData.hero.description} onChange={(e) => setPrivacyData({ ...privacyData, hero: { ...privacyData.hero, description: e.target.value } })}></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Policy Sections Edit */}
                    <div className="sections-list">
                        <div className="sections-header">
                            <h3><FaRegFileAlt /> Policy Sections</h3>
                            <button className="add-section-btn" onClick={addSection}><FaPlus /> Add New Section</button>
                        </div>

                        <AnimatePresence>
                            {privacyData.sections.map((section, index) => (
                                <motion.div
                                    key={section.id}
                                    className="admin-policy-section-card"
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, x: -50 }}
                                >
                                    <div className="card-top">
                                        <h4>Section #{index + 1}</h4>
                                        <button className="delete-btn" onClick={() => removeSection(section.id)}><FaTrash /></button>
                                    </div>

                                    <div className="admin-form-grid">
                                        <div className="admin-input-group">
                                            <label>Icon Type</label>
                                            <select value={section.icon} onChange={(e) => updateSection(section.id, 'icon', e.target.value)}>
                                                <option value="shield">Shield (Security)</option>
                                                <option value="lock">Lock (Data)</option>
                                                <option value="file">File (Policy)</option>
                                                <option value="balance">Balance (Legal)</option>
                                                <option value="user">User (Privacy)</option>
                                            </select>
                                        </div>
                                        <div className="admin-input-group">
                                            <label>Section Title</label>
                                            <input type="text" value={section.title} onChange={(e) => updateSection(section.id, 'title', e.target.value)} />
                                        </div>
                                        <div className="admin-input-group full-width">
                                            <label><FaParagraph /> Content</label>
                                            <textarea rows="4" value={section.content} onChange={(e) => updateSection(section.id, 'content', e.target.value)}></textarea>
                                        </div>

                                        <div className="admin-input-group full-width">
                                            <label><FaListUl /> Key Points (Optional)</label>
                                            <div className="points-manager">
                                                {section.points.map((point, pIdx) => (
                                                    <div key={pIdx} className="point-row">
                                                        <input type="text" value={point} onChange={(e) => updatePoint(section.id, pIdx, e.target.value)} />
                                                        <button className="remove-point" onClick={() => removePoint(section.id, pIdx)}><FaTrash /></button>
                                                    </div>
                                                ))}
                                                <button className="add-point-btn" onClick={() => addPoint(section.id)}><FaPlus /> Add Point</button>
                                            </div>
                                        </div>
                                    </div>
                                </motion.div>
                            ))}
                        </AnimatePresence>
                    </div>

                    <div className="admin-card-section footer-meta">
                        <div className="admin-input-group">
                            <label>Last Updated Date</label>
                            <input type="text" value={privacyData.lastUpdated} onChange={(e) => setPrivacyData({ ...privacyData, lastUpdated: e.target.value })} />
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

export default AdminPrivacy;
